/**
 * @file PlanTypeCategoriesPage.js
 * @description Displays and manages plan type categories with drag and drop functionality for sorting.
 */

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';

import styles from './PlanYear.module.scss';
import {
    fetchPlanTypeDetailCategories,
    updatePlanTypeDetailCategories,
} from '../../services/benefitPlanService';
import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';
const PlanTypeCategoriesPage = () => {
    const { showModal } = useContext(ModalContext);
    const { planTypeId } = useParams();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [originalCategories, setOriginalCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [planTypeName, setPlanTypeName] = useState('');
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    const loadPlanTypeCategories = useCallback(async () => {
        try {
            setIsLoading(true);
            const fetchedCategories =
                await fetchPlanTypeDetailCategories(planTypeId);

            const mappedCategories = fetchedCategories.map(category => ({
                ...category,
                categoryId: category.id,
                categoryName: category.systemPlanDetailCategory?.name,
                isVisible: category.isActive,
            }));

            const sortedCategories = mappedCategories.sort(
                (a, b) => a.sortOrder - b.sortOrder,
            );
            setCategories(sortedCategories);
            setOriginalCategories(sortedCategories);

            const planTypeDisplayName =
                fetchedCategories.length > 0
                    ? fetchedCategories[0].planType?.name
                    : `Plan Type ${planTypeId}`;
            setPlanTypeName(planTypeDisplayName);
        } catch (error) {
            console.error('Failed to fetch plan type categories', error);
            if (error?.response?.data?.detail) {
                Toast.error(error?.response?.data?.detail);
            } else {
                Toast.error(
                    getText('msg_6f4df4eb51ef'),
                );
            }
        } finally {
            setIsLoading(false);
        }
    }, [planTypeId]);

    const checkForChanges = useCallback(
        (currentCategories, originalCategories) => {
            if (currentCategories.length !== originalCategories.length)
                return true;

            return currentCategories.some((category, index) => {
                const original = originalCategories[index];
                return (
                    category.sortOrder !== original.sortOrder ||
                    category.isVisible !== original.isVisible ||
                    category.categoryId !== original.categoryId
                );
            });
        },
        [],
    );

    useEffect(() => {
        if (planTypeId) {
            loadPlanTypeCategories();
        }
    }, [loadPlanTypeCategories, planTypeId]);

    useEffect(() => {
        setHasChanges(checkForChanges(categories, originalCategories));
    }, [categories, originalCategories, checkForChanges]);

    const handleSave = async () => {
        try {
            setIsSaving(true);

            const categoriesToSave = categories.map(category => ({
                id: category.id,
                clientPlanTypeId: category.clientPlanTypeId,
                systemPlanDetailCategoryId: category.systemPlanDetailCategoryId,
                sortOrder: category.sortOrder,
                isActive: category.isVisible,
            }));
            await updatePlanTypeDetailCategories(planTypeId, categoriesToSave);
            setOriginalCategories([...categories]);
            setHasChanges(false);
            Toast.success(getText('msg_2ec0f08cdb55'));
        } catch (error) {
            console.error('Failed to save categories', error);
            Toast.error(getText('msg_36bb53c3d59b'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (hasChanges) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_2f4e8fc9ac09'),
                text: getText('msg_f3cefc556397'),
                onYes: () => navigate(ROUTES.PLAN_TYPES),
                onNo: () => {},
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_36fff63ccbcd'),
            });
        } else {
            navigate(ROUTES.PLAN_TYPES);
        }
    };

    const handleReset = () => {
        setCategories([...originalCategories]);
        setHasChanges(false);
        Toast.info(getText('msg_2930170631a2'));
    };

    const handleDragStart = e => {
        const index = e.target.dataset.index;
        setDraggedIndex(index);
        e.dataTransfer.setData('text/plain', index);
        e.target.classList.add('dragging');
    };

    const handleDragOver = e => {
        e.preventDefault();
    };

    const handleDrop = async e => {
        e.preventDefault();
        const targetIndex = hoveredIndex;

        let newIndex;
        if (draggedIndex < targetIndex) {
            newIndex = targetIndex - 1;
        } else {
            newIndex = targetIndex;
        }

        if (newIndex < 0) {
            newIndex = 0;
        }

        if (draggedIndex !== newIndex) {
            const newCategories = [...categories];
            const [draggedCategory] = newCategories.splice(draggedIndex, 1);
            newCategories.splice(newIndex, 0, draggedCategory);

            const updatedCategories = newCategories.map((category, index) => ({
                ...category,
                sortOrder: index + 1,
            }));

            setCategories(updatedCategories);
        }

        setDraggedIndex(null);
        setHoveredIndex(null);
    };

    const handleDragEnd = e => {
        e.target.classList.remove('dragging');
        setDraggedIndex(null);
        setHoveredIndex(null);
    };

    const handleDropTop = async e => {
        e.preventDefault();
        if (draggedIndex !== null) {
            const newCategories = [...categories];
            const [draggedCategory] = newCategories.splice(draggedIndex, 1);
            newCategories.unshift(draggedCategory);

            const updatedCategories = newCategories.map((category, index) => ({
                ...category,
                sortOrder: index + 1,
            }));

            setCategories(updatedCategories);
        }
        setDraggedIndex(null);
        setHoveredIndex(null);
    };

    const handleToggleVisibility = category => {
        const updatedCategories = categories.map(cat =>
            cat.categoryId === category.categoryId
                ? {
                      ...cat,
                      isVisible: !cat.isVisible,
                      isActive: !cat.isVisible,
                  }
                : cat,
        );
        setCategories(updatedCategories);
    };

    const renderCategories = () => {
        const items = [];

        items.push(
            <div
                key="top-drop-zone"
                className={`mb-3 d-flex align-items-center justify-content-center rounded ${
                    hoveredIndex === -1
                        ? 'bg-info bg-opacity-10 border border-2 border-primary border-opacity-50 p-4'
                        : 'p-1'
                }`}
                onDragOver={handleDragOver}
                onDrop={handleDropTop}
                onDragEnter={() => setHoveredIndex(-1)}
                onDragLeave={() => setHoveredIndex(null)}
            >
                {hoveredIndex === -1 && (
                    <span className="text-muted">
                        <i className="bi bi-arrow-up me-2" />
                        {getText('msg_9dfd765eec6b')}
                    </span>
                )}
            </div>,
        );

        categories.forEach((category, index) => {
            items.push(
                <div key={category.categoryId} className="mb-3">
                    <div className="row align-items-center">
                        <div className="col-9">
                            <div
                                className={`card shadow-sm rounded-3 ${
                                    hoveredIndex === index
                                        ? 'border-primary border-2'
                                        : ''
                                }`}
                                draggable
                                data-index={index}
                                onDragStart={handleDragStart}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onDragEnd={handleDragEnd}
                                onDragEnter={() => setHoveredIndex(index)}
                                onDragLeave={() => setHoveredIndex(null)}
                            >
                                <div className="card-body pe-0 py-2">
                                    <div className="d-flex align-items-center">
                                        <div className="me-2">
                                            <i className="bi bi-grip-vertical text-muted fs-5" />
                                        </div>
                                        <div className="d-flex">
                                            <h5 className="card-title mb-0 align-self-center">
                                                {category.categoryName}
                                            </h5>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-link text-decoration-none align-self-center ms-3"
                                                onClick={() =>
                                                    navigate(
                                                        `/plan-types/${planTypeId}/categories/${category.id}`,
                                                    )
                                                }
                                            >
                                                {getText('msg_40e91027f1df')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-3">
                            <div className="d-flex justify-content-center">
                                <div className="form-switch form-switch-locked">
                                    <input
                                        className="form-check-input custom-switch-input"
                                        type="checkbox"
                                        checked={category.isVisible}
                                        onChange={() =>
                                            handleToggleVisibility(category)
                                        }
                                        onClick={e => e.stopPropagation()}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
            );

            items.push(
                <div
                    key={`drop-zone-${index}`}
                    className={`mb-2 d-flex align-items-center justify-content-center rounded ${
                        hoveredIndex === index + 1
                            ? 'bg-info bg-opacity-10 border border-2 border-primary border-opacity-50 p-3'
                            : 'p-1'
                    }`}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnter={() => setHoveredIndex(index + 1)}
                    onDragLeave={() => setHoveredIndex(null)}
                >
                    {hoveredIndex === index + 1 && (
                        <span className="text-muted small">
                            <i className="bi bi-arrow-down me-2" />
                            {getText('msg_7c67e6fb70f3')}
                        </span>
                    )}
                </div>,
            );
        });

        return items;
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100%',
            }}
        >
            <div
                className="page-content"
                style={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div
                    className="card"
                    style={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div
                        className="card-body px-0 py-4"
                        style={{ flexGrow: 1 }}
                    >
                        <div className="ms-3 me-4 d-flex justify-content-between align-items-center py-3">
                            <div className="d-flex flex-column align-items-start mb-4">
                                <p className="page-title card-title ms-4 mb-0">
                                    {getText('msg_81fe1a2936d8')} {planTypeName}
                                </p>
                                <p
                                    className="ing-portals-subtitle mt-1"
                                    style={{ marginTop: '10px' }}
                                >
                                    {getText('msg_fa9e50a4230a')}
                                    <br />
                                    {getText('msg_f50dd1561d3b')}
                                </p>
                            </div>
                            <div className="float-end d-flex align-items-center align-self-baseline">
                                <span
                                    type="button"
                                    onClick={handleReset}
                                    className="ing-text-buttons mx-3"
                                    style={{
                                        opacity: hasChanges ? 1 : 0.5,
                                        pointerEvents: hasChanges
                                            ? 'auto'
                                            : 'none',
                                    }}
                                >
                                    <i className="bi bi-arrow-counterclockwise me-2" />
                                    {getText('msg_44c57abd888a')}
                                </span>
                                <span
                                    type="button"
                                    onClick={handleSave}
                                    className="ing-text-buttons me-3 d-flex"
                                    style={{
                                        opacity: hasChanges ? 1 : 0.5,
                                        pointerEvents: hasChanges
                                            ? 'auto'
                                            : 'none',
                                    }}
                                >
                                    {isSaving ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            {getText('msg_ae7e887517b0')}
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-check me-2" />
                                            {getText('msg_efc007a393f6')}
                                        </>
                                    )}
                                </span>
                                <span
                                    type="button"
                                    onClick={handleClose}
                                    className="ing-text-buttons me-3 d-flex"
                                    tabIndex={12}
                                >
                                    <i className="bi bi-x me-2" />
                                    {getText('msg_bbfa773e5a63')}
                                </span>
                            </div>
                        </div>

                        {isLoading ? (
                            <div className="text-center mt-5">
                                <Spinner
                                    width={50}
                                    className={styles.loadingSpinner}
                                />
                            </div>
                        ) : categories.length === 0 ? (
                            <NotFoundBox
                                backgroundColor="#f0f4ff"
                                title={getText('msg_31994b16cf28')}
                                description={getText('msg_2a4cb4e038b0')}
                            />
                        ) : (
                            <div className="container-fluid px-4">
                                <div className="row justify-content-center">
                                    <div className="col-12 col-lg-8 mt-4">
                                        <div className="row align-items-center">
                                            <div className="col-9"></div>
                                            <div className="col-3">
                                                <div className="d-flex justify-content-center">
                                                    <h6 className="mb-0">
                                                        {getText('msg_1fe59390acc1')}
                                                    </h6>
                                                </div>
                                            </div>
                                        </div>

                                        {renderCategories()}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlanTypeCategoriesPage;
